import {Button, LabelRegular, Loading, TextRegular, TextSemiBold} from '@hm/fundamental'
import {mdiClose, mdiPencil} from '@mdi/js'
import {useEffect, useRef, useState} from 'react'

import {RiaCommentsContainer} from '../../containers/RiaCommentsContainer'
import {formatDate, formatDateOnly, formatNumber} from '../../helpers'
import {
  useRiaAuditLog,
  useRiaDetail,
  useRiaDetailsAuditLog,
  useRiskSurveyTranslation
} from '../../hooks'
import {RiaPriorityPill, RiaStatusPill} from '../plant'

import {AuditLogSection} from './AuditLogSection'
import {RiaDetailsAuditLogSection} from './RiaDetailsAuditLogSection'
import {RiaEditForm} from './RiaEditForm'

type RiaDetailPanelProps = {
  riaId: string
  open: boolean
  onClose: () => void
  canManage: boolean
}

// The component intentionally renders multiple conditional UI sections in one
// place to keep drawer state handling cohesive.
// eslint-disable-next-line complexity
export const RiaDetailPanel = ({riaId, open, onClose, canManage}: RiaDetailPanelProps) => {
  const {t, i18n} = useRiskSurveyTranslation()
  const {data: ria, isLoading} = useRiaDetail(riaId, {enabled: open && !!riaId})
  const {data: auditLog} = useRiaAuditLog(riaId, {enabled: open && !!riaId})
  const {data: detailsAuditLog} = useRiaDetailsAuditLog(riaId, {enabled: open && !!riaId})
  const closeButtonRef = useRef<HTMLButtonElement | null>(null)
  const [isEditing, setIsEditing] = useState(false)

  useEffect(() => {
    if (open && !isLoading) {
      closeButtonRef.current?.focus()
    }
  }, [open, isLoading])

  // Reset to read-only whenever the panel opens or switches RIA.
  useEffect(() => {
    setIsEditing(false)
  }, [riaId, open])

  const leDelta = ria?.leDelta
  const hasLeDelta = leDelta !== null && leDelta !== undefined
  const deltaColorClass = hasLeDelta ? (leDelta >= 0 ? 'text-positive' : 'text-negative') : ''

  if (!open) {
    return null
  }

  return (
    <div
      data-cy="ria-detail-panel"
      className="shrink-0 w-[470px] max-w-full h-full relative overflow-hidden rounded bg-container_background text-foreground shadow-md"
    >
      <div className="p-4 h-full overflow-y-auto">
        {isLoading ? (
          <div className="flex justify-center items-center min-h-[240px]">
            <Loading variant="primary" size="large" />
          </div>
        ) : !ria ? (
          <TextRegular className="p-4">{t('common.error')}</TextRegular>
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between mb-4 gap-2">
              <div className="flex flex-col min-w-0">
                <LabelRegular className="text-foreground_faded">{ria.riNumber}</LabelRegular>
                <TextSemiBold>{ria.title}</TextSemiBold>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {canManage && !isEditing && (
                  <Button
                    variant="secondary"
                    icon={mdiPencil}
                    round
                    square
                    aria-label={t('detail.edit')}
                    title={t('detail.edit')}
                    onClick={() => setIsEditing(true)}
                    data-cy="edit-button"
                  />
                )}
                <Button
                  ref={closeButtonRef}
                  variant="secondary"
                  icon={mdiClose}
                  round
                  square
                  aria-label={t('common.close')}
                  onClick={onClose}
                  data-cy="detail-close-button"
                />
              </div>
            </div>

            <hr className="border-border mb-2" />

            {/* Properties */}
            <div className="flex flex-col gap-3 mb-4">
              {!isEditing && (
                <div className="flex justify-between items-center">
                  <LabelRegular className="text-foreground_faded text-sm">
                    {t('detail.status')}
                  </LabelRegular>
                  <RiaStatusPill status={ria.status} />
                </div>
              )}
              <div className="flex justify-between items-center">
                <LabelRegular className="text-foreground_faded text-sm">
                  {t('detail.priority')}
                </LabelRegular>
                <RiaPriorityPill priority={ria.priority} />
              </div>
              <div className="flex justify-between">
                <LabelRegular className="text-foreground_faded text-sm">
                  {t('detail.costClassification')}
                </LabelRegular>
                <LabelRegular className="text-sm">
                  {t(`costClassification.${ria.costClassification}`)}
                </LabelRegular>
              </div>
              <div className="flex justify-between">
                <LabelRegular className="text-foreground_faded text-sm">
                  {t('table.lePrior')}
                </LabelRegular>
                <LabelRegular className="text-sm">
                  {formatNumber(ria.lePrior, i18n.language)}
                </LabelRegular>
              </div>
              <div className="flex justify-between">
                <LabelRegular className="text-foreground_faded text-sm">
                  {t('table.leAfter')}
                </LabelRegular>
                <LabelRegular className="text-sm">
                  {formatNumber(ria.leAfter, i18n.language)}
                </LabelRegular>
              </div>
              <div className="flex justify-between">
                <LabelRegular className="text-foreground_faded text-sm">
                  {t('table.leDelta')}
                </LabelRegular>
                <LabelRegular className="font-semibold text-sm">
                  <span className={deltaColorClass}>
                    {formatNumber(ria.leDelta, i18n.language)}
                  </span>
                </LabelRegular>
              </div>
              <div className="flex justify-between">
                <LabelRegular className="text-foreground_faded text-sm">
                  {t('table.indicativeTimeScale')}
                </LabelRegular>
                <LabelRegular className="text-sm">
                  {ria.indicativeTimeScale
                    ? formatDate(ria.indicativeTimeScale, i18n.language)
                    : '—'}
                </LabelRegular>
              </div>
              {!isEditing && (
                <>
                  <div className="flex justify-between">
                    <LabelRegular className="text-foreground_faded text-sm">
                      {t('detail.responsiblePerson')}
                    </LabelRegular>
                    <LabelRegular className="text-sm">
                      {ria.responsiblePerson ? t(`responsibleRole.${ria.responsiblePerson}`) : '—'}
                    </LabelRegular>
                  </div>
                  <div className="flex justify-between">
                    <LabelRegular className="text-foreground_faded text-sm">
                      {t('detail.dueDate')}
                    </LabelRegular>
                    <LabelRegular className="text-sm">
                      {ria.dueDate ? formatDateOnly(ria.dueDate, i18n.language) : '—'}
                    </LabelRegular>
                  </div>
                </>
              )}
            </div>

            <hr className="border-border mb-2" />

            {/* Edit form (managers) — opened from the header Edit action. */}
            {canManage && isEditing && (
              <>
                <TextSemiBold className="mb-2">{t('detail.editTitle')}</TextSemiBold>
                <RiaEditForm
                  riaId={riaId}
                  currentStatus={ria.status}
                  currentResponsiblePerson={ria.responsiblePerson}
                  currentDueDate={ria.dueDate}
                  onDone={() => setIsEditing(false)}
                />
                <hr className="border-border my-2" />
              </>
            )}

            {/* Comments */}
            <RiaCommentsContainer riaId={riaId} canManage={canManage} />

            <hr className="border-border my-2" />

            {/* Audit log */}
            <AuditLogSection entries={auditLog ?? []} />

            {/* Details change history */}
            <RiaDetailsAuditLogSection entries={detailsAuditLog ?? []} />
          </>
        )}
      </div>
    </div>
  )
}
